import { useEffect, useState } from "react";
import { useErrorHandler } from "../../hooks/useErrorHandler";
import { ErrorMessage } from "../../components/error-message";
import CrudTable from "../../components/crud-table";
import { PaymentsAPI } from "../../APIs/payments.api";
import { CrudTableHeader } from "../../components/crud-table-header";
import { useTableControls } from "../../hooks/useTableControls";
import { useCompetitions } from "../../context/CompetitionsContext";
import Label from "../../components/label";
import { TeamLink } from "../../components/team-link";
import { Loading } from "../../components/loading";
import { useNavigate } from "react-router-dom";
import { ArrowLeftRight } from "lucide-react";

interface TransferPaymentWithDetails {
  id: number;
  payment_id: number;
  sender_first_name: string;
  sender_last_name: string;
  sender_cuit: string;
  sender_cvu_cbu?: string;
  transfer_reference?: string;
  transfer_created_at: string;
  confirmation_status: "pending" | "confirmed" | "rejected";

  payment_status: string;
  payment_amount: string;
  team_name: string;
  team_id: number;
  registration_number: number;
}

export function PendingTransfersTable() {
  const [transfers, setTransfers] = useState<TransferPaymentWithDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { error, handleError, clearError } = useErrorHandler();
  const { currentCompetitionId } = useCompetitions();
  const navigate = useNavigate();

  const {
    paginatedData: paginatedTransfers,
    filteredData,
    totalPages,
    currentPage,
    sortConfig,
    setSortConfig,
    setSearchQuery,
    setSelectedFilters,
    setCurrentPage,
  } = useTableControls<TransferPaymentWithDetails>(transfers || []);

  const columns = [
    {
      key: "id" as keyof TransferPaymentWithDetails,
      label: "N° Transfer",
      sortable: true,
    },
    {
      key: "team_name" as keyof TransferPaymentWithDetails,
      label: "Equipo",
      sortable: true,
      render: (value: string, row: TransferPaymentWithDetails) => (
        <TeamLink teamName={value} teamId={row.team_id} />
      ),
    },
    {
      key: "registration_number" as keyof TransferPaymentWithDetails,
      label: "Nº Reg.",
      sortable: true,
    },
    {
      key: "sender_first_name" as keyof TransferPaymentWithDetails,
      label: "Remitente",
      sortable: true,
      render: (value: string, row: TransferPaymentWithDetails) => (
        <div>
          <div className="font-medium">
            {value} {row.sender_last_name}
          </div>
          {row.sender_cuit && (
            <div className="text-xs text-gray-500 font-mono">
              {row.sender_cuit}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "payment_amount" as keyof TransferPaymentWithDetails,
      label: "Monto",
      sortable: true,
      render: (value: string) => (
        <Label color="gray">
          $ {parseFloat(String(value)).toLocaleString("es-AR")}
        </Label>
      ),
    },

    {
      key: "confirmation_status" as keyof TransferPaymentWithDetails,
      label: "Estado",
      sortable: true,
      render: (value: string) => <Label color="yellow">Pendiente</Label>,
    },
    {
      key: "transfer_created_at" as keyof TransferPaymentWithDetails,
      label: "Registrado",
      sortable: true,
      render: (value: string) =>
        new Date(value).toLocaleString("es-AR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }),
    },
  ];

  useEffect(() => {
    if (!currentCompetitionId) {
      return;
    }
    const fetchPendingTransfers = async () => {
      clearError();
      setIsLoading(true);
      try {
        const response = await PaymentsAPI.getPendingTransfers(
          currentCompetitionId
        );
        if (response.statusCode === 200) {
          setTransfers(response.data);
        } else {
          handleError(response.message, { persist: true });
        }
      } catch (err) {
        handleError("Error de conexión, intente nuevamente.", {
          persist: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchPendingTransfers();
  }, [handleError, currentCompetitionId, clearError]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleSort = (
    sortBy: string | null,
    sortDirection: "asc" | "desc" | null
  ) => {
    setSortConfig({
      sortBy: sortBy as keyof TransferPaymentWithDetails | null,
      sortDirection,
    });
  };

  const handleFilterChange = (selectedFilters: Record<string, string[]>) => {
    setSelectedFilters(selectedFilters);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="w-full p-8">
      <CrudTableHeader
        title="Transferencias a confirmar"
        count={transfers?.length}
        icon={<ArrowLeftRight />}
      />

      {error && (
        <div className="mb-4">
          <ErrorMessage message={error} dismissible />
        </div>
      )}

      {isLoading ? (
        <Loading />
      ) : transfers ? (
        <CrudTable
          data={paginatedTransfers}
          totalCount={filteredData.length}
          columns={columns}
          search={{
            onSearch: handleSearch,
            placeholder: "Buscar transferencias...",
          }}
          sort={{
            sortBy: sortConfig.sortBy,
            sortDirection: sortConfig.sortDirection,
            onSort: handleSort,
          }}
          pagination={{
            page: currentPage,
            totalPages: totalPages,
            onPageChange: handlePageChange,
          }}
          filters={[
            {
              key: "payment_status",
              label: "Estado de Pago",
              multiple: true,
              options: [
                { key: "pending", value: "Pendiente" },
                { key: "approved", value: "Aprobado" },
                { key: "rejected", value: "Rechazado" },
              ],
            },
          ]}
          onFilterChange={handleFilterChange}
          actions={{
            view: {
              label: "",
              onClick: (row: TransferPaymentWithDetails) =>
                navigate(`/dashboard/pending-transfers/view/${row.payment_id}`),
            },
          }}
        />
      ) : null}
    </div>
  );
}
